import React, { useState } from 'react'
import { cloneDeep } from 'lodash'

import MainModal from '../../../../../components/modals/common/MainModal'
import { Separator } from '../../../../../components/ui/separator'
import { PublishNotificationsItem } from './PublishNotificationsItem'

import { getConvertedData } from '../../../../../models/interface.helper'

import { EventReviewPublishModel } from '../../../../../models/event-review-publish/event-review-publish.model'

import { IRegistrationByDayInterface } from '../../../../../models/registrations-by-day/registrationByDay.interface'
import { PublishItem } from '../../ManageClinicNOtherOperationsTab/types/publish.types'
import { TGroupPublish } from '../../../../../models/event-scheduling/event-scheduling.interface'

import { MODAL_CONSTS } from '../../../../../const/modal-const'
import FirestoreService from '../../../../../services/firestoreService'
import { CONST } from '../../../../../const/const'

const COLLECTIONS = CONST.DATA.FIRESTORE.V01.COLLECTIONS

interface PublishNotificationsModalProps {
  show: boolean
  handleModal: (showHide: boolean, typeOfModal: string, data?: any) => void
  dataToPassOn?: {
    publishItems: PublishItem[]
    onSavePublish: (state: TGroupPublish) => Promise<void>
    eventId: string
    registrationsGroupedByDay?: Record<string, any[]>
    registrationsByDay?: IRegistrationByDayInterface[]
    scheduleBy: 'class' | 'rider'
  }
}

export const PublishNotificationsModal: React.FC<PublishNotificationsModalProps> = ({
  show,
  handleModal,
  dataToPassOn,
}) => {
  const {
    publishItems = [],
    onSavePublish,
    eventId,
    registrationsGroupedByDay = {},
    registrationsByDay = [],
    scheduleBy = 'rider',
  } = dataToPassOn || {}

  const [searchTerm, setSearchTerm] = useState('')
  const [isSaving, setIsSaving] = useState(false)
  const [lastSentTimes, setLastSentTimes] = useState<Record<string, string>>({})
  const [publishState, setPublishState] = useState<TGroupPublish>(() => {
    if (!publishItems) {
      return {}
    }
    // create initial state from passed data
    return publishItems.reduce((acc, item) => {
      acc[item.id] = {
        publishOrder:
          publishItems.find((publishItem) => publishItem.id === item.id)?.orderOfGoStatus ??
          item.orderOfGoStatus,
        publishScore:
          publishItems.find((publishItem) => publishItem.id === item.id)?.scoresStatus ??
          item.scoresStatus,
      }
      return acc
    }, {} as TGroupPublish)
  })

  const filteredData = publishItems.filter(
    (item) =>
      item.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.subtitle.toLowerCase().includes(searchTerm.toLowerCase())
  )

  const closeModal = () => {
    handleModal(false, MODAL_CONSTS.PUBLISH_NOTIFICATIONS_MODAL)
  }

  const handleSave = async () => {
    if (isSaving) return

    try {
      setIsSaving(true)

      const publishedEventInDbSnapShot = await FirestoreService.getItem(
        COLLECTIONS.EVENT_REVIEW_PUBLISH.NAME,
        eventId!
      )

      const publishedEventInDb = getConvertedData(
        EventReviewPublishModel.fromFirestoreDoc(publishedEventInDbSnapShot).toObject()
      )
      const EventSchedule = publishedEventInDb?.EventSchedule

      let type: 'riderGroupPublish' | 'classGroupPublish' = 'classGroupPublish'

      if (scheduleBy === 'rider') {
        type = 'riderGroupPublish'
      }

      const scheduleByType = cloneDeep(EventSchedule?.[type] || {})

      Object.keys(publishState).forEach((itemId) => {
        scheduleByType[itemId] = {
          ...scheduleByType[itemId],
          ...publishState[itemId],
        }
      })

      const updated = {
        EventSchedule: {
          ...EventSchedule,
          [type]: scheduleByType,
        },
      }

      await FirestoreService.updateItem(COLLECTIONS.EVENT_REVIEW_PUBLISH.NAME, eventId, updated)
      await FirestoreService.updateItem(COLLECTIONS.EVENT_SCHEDULING.NAME, eventId, {
        [type]: scheduleByType,
      })

      await onSavePublish?.(scheduleByType)
      closeModal()
    } catch (error) {
      console.error('Error saving publish state:', error)
    } finally {
      setIsSaving(false)
    }
  }
  if (!show) return null

  if (!publishItems || publishItems.length === 0) {
    return (
      <MainModal show={show} onHide={closeModal} type="PUBLISH_NOTIFICATIONS_MODAL" size="xl">
        <div className="space-y-6">
          <div className="space-y-3">
            <h1 className="text-2xl text-SeabiscuitDark200ThemeColor">
              Publish and send notifications
            </h1>
            <p className="text-SeabiscuitDark200ThemeColor/60">No publish data available.</p>
          </div>
        </div>
      </MainModal>
    )
  }

  return (
    <MainModal
      show={show}
      onHide={closeModal}
      type="PUBLISH_NOTIFICATIONS_MODAL"
      size="xl"
      footerClassName="flex !flex-row justify-end !items-end !px-3 !pt-4 !pb-0 md:px-[0] !gap-3"
      buttons={[
        {
          label: 'Cancel',
          onClick: closeModal,
          bgClass: 'bg-white',
          textClass: 'text-SeabiscuitDark200ThemeColor/60',
          borderClass: 'border-transparent border',
          onHoverClass: 'hover:bg-gray-50 hover:text-gray-800',
          fullWidth: false,
          className: '!w-[150px]',
        },
        {
          label: isSaving ? 'Saving...' : 'Save',
          onClick: handleSave,
          bgClass: 'bg-SeabiscuitMainThemeColor',
          disabled: isSaving,
          textClass: 'text-white',
          onHoverClass: 'hover:bg-SeabiscuitMainThemeColor/80 hover:text-white',
          fullWidth: false,
          className: '!w-[150px]',
        },
      ]}
    >
      <div className="space-y-6">
        <div className="space-y-2 ">
          <h1 className="text-2xl text-SeabiscuitDark200ThemeColor">
            Publish and send notifications
          </h1>
          <div className="space-y-1.5 ">
            <h2 className="text-SeabiscuitDark200ThemeColor text-md">
              Toggle on to publish to the single event page.
            </h2>
            <h3 className="text-SeabiscuitDark200ThemeColor text-sm">
              Click bell to notify riders of changes to times and scores
            </h3>
          </div>
        </div>
        <Separator className="bg-SeabiscuitGray500ThemeColor" />

        <div className="relative">
          <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
            <img src={'assets/cp_icons/search-theme-icon.svg'} alt="search" className="h-4 w-4" />
          </div>
          <input
            type="text"
            placeholder={
              scheduleBy === 'rider' ? 'Search day or facility' : 'Search by class or facility'
            }
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="block w-full border py-2 pl-5 pr-10 border-SeabiscuitGray500ThemeColor rounded-full text-md placeholder:text-md placeholder:text-SeabiscuitDark200ThemeColor/50 focus:outline-none focus:border-SeabiscuitGray600ThemeColor transition-colors text-SeabiscuitDark200ThemeColor"
          />
        </div>

        <div className="space-y-6 pb-4">
          {filteredData.length > 0 ? (
            filteredData.map((item) => {
              return (
                <PublishNotificationsItem
                  key={item.id}
                  item={item}
                  eventId={eventId}
                  publishItems={publishItems}
                  publishState={publishState}
                  setPublishState={setPublishState}
                  isSaving={isSaving}
                  scheduleBy={scheduleBy}
                  registrationsByDay={registrationsByDay}
                  registrationsGroupedByDay={registrationsGroupedByDay}
                  setLastSentTimes={setLastSentTimes}
                  lastSentTimes={lastSentTimes}
                />
              )
            })
          ) : (
            <div className="text-center py-8">
              <p className="text-SeabiscuitDark200ThemeColor/60">No items match your search.</p>
            </div>
          )}
        </div>
      </div>
    </MainModal>
  )
}
